import {Inject, Injectable} from '@angular/core';
import {HttpInterceptorService} from '@ispm/core/services/http-interceptor.service';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {HttpErrorResponse} from '@angular/common/http';

import {OrganizationEntity} from '../classes/OrganizationEntity';
import {OrganizationTypeEntity} from '../classes/OrganizationTypeEntity';
import {BrowserCacheService} from "@ispm/core/services/browser-cache.service";
import {Store} from '@ngrx/store';
import {IOrganization} from "../interfaces";
import { AuthenticationService } from '@ispm/user/services/authentication.service';
import { NotificationMessageService } from '@ispm/core/components/customise-notification/notifications-message.service';

@Injectable({providedIn: 'root'})

export class OrgAdminOrganizationService {

  static readonly valueSetBaseUrl = '/valueset';
  static readonly valueSetDocumentsUrl = '/reportsAndDocuments';
  static readonly baseUrl = '/organization';
  static readonly baseIAMUrl = '/organization/iam';
  public store$: Store<any>;
  private coreStore$: Store<any>;

  constructor(
    private browserCacheService: BrowserCacheService,
    private notificationMessageService: NotificationMessageService,
    private authService: AuthenticationService,
    private _http: HttpInterceptorService,
    private store: Store<any>
  ) {
    this.store$ = store.select('users') as any;
    this.coreStore$ = store.select('core') as any;
  }

  /**
   * Get organization types with additional types
   */
  public getOrganizationTypesWithPadding(): Observable<OrganizationTypeEntity[]> {
    return this.getOrganizationTypes(true);
  }

  /**
   * Get organization types without additional types
   */
  public getOrganizationTypesWithoutPadding(): Observable<OrganizationTypeEntity[]> {
    return this.getOrganizationTypes(false);
  }

  public test(): Observable<any> {
    return this.coreStore$.select('environmentDetails');
  }

  /**
   * Get organization types
   *
   * @param withPadding if true than will added additional types
   */
  protected getOrganizationTypes(withPadding: boolean): Observable<OrganizationTypeEntity[]> {
    const req = this._http.get(OrgAdminOrganizationService.valueSetBaseUrl + '/getOrganizationTypes', {}, true)
      .map((res: any) => {
        return res.json();
      })
      .map((data: any) => {
        let orgTypes: OrganizationTypeEntity[] = [];

        if (withPadding) {
          orgTypes = [
            new OrganizationTypeEntity({
              code: '',
              display: 'All',
              definition: ''
            }),
            new OrganizationTypeEntity({
              code: 'prov,lab',
              display: 'Healthcare Provider or Clinical Lab',
              definition: ''
            }),
          ];
        }

        data.forEach((orgType: OrganizationTypeEntity) => {
          orgTypes.push(new OrganizationTypeEntity({
              code: orgType.code,
              display: orgType.display,
              definition: orgType.definition
            })
          );
        });

        return orgTypes;
      })
      .catch((err: HttpErrorResponse) => {
        this.notificationMessageService.error('Failed to get organization types.');
        console.error(`Error getting organization types: ${JSON.stringify(err)}`);

        return Observable.of([new OrganizationTypeEntity()]);
      })
      .publish();

    req.connect();
    return req;
  }

  /**
   * Get organizations by params
   *
   * @param orgType
   * @param orgSearchVal
   */
  public getOrganizations(orgType: string = '', orgSearchVal: string = '', orgId: string = ''): Observable<OrganizationEntity[]> {
    const params = {
      _count: 100000,
      _fieldslist: 'id,name,externalId',
      _sort: 'name',
      cacheId: this.browserCacheService.getCacheId(),
      name: "",
    };
    if (orgType) {
      params['type'] = orgType;
    }
    if (orgSearchVal) {
      params['name'] = orgSearchVal;
    }
    if (orgId) {
      params['id'] = orgId;
    }
    const req = this._http.get(OrgAdminOrganizationService.baseUrl, params, true)
      .map((res: any) => {
        return res.json();
      })
      .map((data: any) => {
        const orgs: OrganizationEntity[] = [];

        if (data instanceof Array) {
          data.forEach((org: OrganizationEntity) => {
            orgs.push(new OrganizationEntity({
                id: org.id,
                name: org.name,
              })
            );
          });
        } else {
          //For some reason when getting organization by id a flat object is returned (and not an arry).
          //Adding this object to an array.
          orgs.push(new OrganizationEntity({
            id: data.id,
            name: data.name
          }));
        }
        return orgs;
      })
      .catch((err: HttpErrorResponse) => {
        this.notificationMessageService.error('Failed to get organizations lists.');
        console.error(`Error getting organizations lists: ${JSON.stringify(err)}`);

        return Observable.of([new OrganizationEntity()]);
      })
      .publish();

    req.connect();
    return req;
  }

  /**
   * Get organization data by Id
   *
   * @param organizationId
   */
  public getOrganizationData(organizationId: string): Observable<any> {
    if (!organizationId || organizationId.toUpperCase() === 'CREATE') {
      return Observable.of(new OrganizationEntity());
    }

    const req = this._http.get(OrgAdminOrganizationService.baseUrl + '/' + organizationId, {}, true)
      .map((res: any) => {
        return res.json();
      })
      .map((data: any) => {
        return new OrganizationEntity(data);
      })
      .catch((err: HttpErrorResponse) => {
        this.notificationMessageService.error('Failed to get organization data.');
        console.error(`Error getting organization data: ${JSON.stringify(err)}`);

        return Observable.throwError(new OrganizationEntity());
      })
      .publish();

    req.connect();
    return req;
  }

  public updateOrganizationData(organizationData/*: IOrganization*/): Observable<any/*OrganizationEntity*/> {
    const req = this._http.put(OrgAdminOrganizationService.baseUrl + '/' + organizationData.id, organizationData, true)
      .map((res: any) => {
        return res.json();
      })
      .map((data: any) => {
        return new OrganizationEntity(data);
      })
      .catch((err: HttpErrorResponse) => {
        return Observable.of(err);
      })
      .publish();

    req.connect();
    return req;
  }

  public addOrganizationData(organizationData: any/*: IOrganization*/, parentOrgId: string): Observable<any/*OrganizationEntity*/> {
    const headers = {};
    const options = {};
    let apiUrl = OrgAdminOrganizationService.baseUrl + '/' + parentOrgId;

    if (organizationData.id) {
      headers['explicit-id'] = "true";
      options['parentOrgId'] = parentOrgId;
      apiUrl = OrgAdminOrganizationService.baseUrl;
    }

    const req = this._http.post(apiUrl, organizationData, true, headers, {}, options)
      .map((res: any) => {
        return res.json();
      })
      .map((data: any) => {
        return new OrganizationEntity(data);
      })
      .catch((err: HttpErrorResponse) => {
        this.notificationMessageService.error('Failed to add organization data');
        console.error("### Failed to add organization data ERROR => ", err);
        return Observable.of(err);
      })
      .publish();

    req.connect();
    return req;
  }

  public submitIFU(file: File, id: string) {
    const fd = new FormData();
    fd.append('file', file, file.name);

    const req = this._http.post(OrgAdminOrganizationService.valueSetDocumentsUrl + '/uploadIfu', fd, true,
      {
        'managing-org-id': id
      })
      .map(() => {
        this.notificationMessageService.success('IFU uploaded successfully');

        return null;
      })
      .catch((err: HttpErrorResponse) => {
        console.error('Error getting uploading IFU file:', JSON.stringify(err));

        if (err.status === 500) {
          this.notificationMessageService.error('Could not upload IFU. ' + err.error);
        } else {
          this.notificationMessageService.error('Could not upload IFU');
        }

        return Observable.throwError(err);
      })
      .publish();

    req.connect();
    return req;
  }

  getCurrentOrganizationFromStore(): Observable<IOrganization> {
    return this.store$.select('organizationConfiguration');
  }

  public IsRootUserLoggedIn(): BehaviorSubject<boolean> {
    let isRootUserLoggedIn$ = new BehaviorSubject<boolean>(false);
    this.store$.subscribe(data => {
      if (data && data.currentUser && data.organizationConfiguration) {
        isRootUserLoggedIn$.next(data.currentUser.originalOrganization === data.organizationConfiguration?.partOf?.reference);
      }
    });
    return isRootUserLoggedIn$;
  }

  public getIAMOrganizationData(iamOrgId: string): Observable<any> {
    let apiUrl = OrgAdminOrganizationService.baseIAMUrl + '/' + iamOrgId;
    const req = this._http.get(apiUrl, {}, true)
      .map((data: any) => {
        return data.json();
      })
      .catch((err: HttpErrorResponse) => {
        return Observable.of(err);
      })
      .publish();

    req.connect();
    return req;
  }
}
